import React, { useState, useEffect, useLayoutEffect, useRef, forwardRef, useMemo } from 'react';
import {
  DndContext, closestCenter, MouseSensor, TouchSensor, useSensor, useSensors,
} from '@dnd-kit/core';
import {
  SortableContext, verticalListSortingStrategy, useSortable, arrayMove, defaultAnimateLayoutChanges,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { getProfil, rebuildDokument, updateStelle } from '../api';
import { getTemplate, inferTemplateId } from '../templates';
import { normalisiereCvMarkierungen } from '../utils/cvHighlighter';
import FotoPickerModal from './FotoPickerModal';
import FotoCropperModal from './FotoCropperModal';

const UndoIcon = ({ size = 28 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
    <path opacity="0.4" d="M7.81 2H16.18C19.83 2 22 4.17 22 7.81V16.18C22 19.82 19.83 21.99 16.19 21.99H7.81C4.17 22 2 19.83 2 16.19V7.81C2 4.17 4.17 2 7.81 2Z"/>
    <path d="M13.92 8.97957H8.77L9.1 8.64957C9.39 8.35957 9.39 7.87957 9.1 7.58957C8.81 7.29957 8.33 7.29957 8.04 7.58957L6.47 9.15957C6.18 9.44957 6.18 9.92957 6.47 10.2196L8.04 11.7896C8.19 11.9396 8.38 12.0096 8.57 12.0096C8.76 12.0096 8.95 11.9396 9.1 11.7896C9.39 11.4996 9.39 11.0196 9.1 10.7296L8.84 10.4696H13.92C15.2 10.4696 16.25 11.5096 16.25 12.7996C16.25 14.0896 15.21 15.1296 13.92 15.1296H9C8.59 15.1296 8.25 15.4696 8.25 15.8796C8.25 16.2896 8.59 16.6296 9 16.6296H13.92C16.03 16.6296 17.75 14.9096 17.75 12.7996C17.75 10.6896 16.03 8.97957 13.92 8.97957Z"/>
  </svg>
);

const RedoIcon = ({ size = 28 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
    <path opacity="0.4" d="M7.81 2H16.18C19.83 2 22 4.17 22 7.81V16.18C22 19.82 19.83 21.99 16.19 21.99H7.81C4.17 22 2 19.83 2 16.19V7.81C2 4.17 4.17 2 7.81 2Z"/>
    <path d="M17.53 9.15957L15.96 7.58957C15.67 7.29957 15.19 7.29957 14.9 7.58957C14.61 7.87957 14.61 8.35957 14.9 8.64957L15.23 8.97957H10.08C7.97 8.97957 6.25 10.6996 6.25 12.8096C6.25 14.9196 7.97 16.6396 10.08 16.6396H15C15.41 16.6396 15.75 16.2996 15.75 15.8896C15.75 15.4796 15.41 15.1396 15 15.1396H10.08C8.8 15.1396 7.75 14.0996 7.75 12.8096C7.75 11.5196 8.79 10.4796 10.08 10.4796H15.16L14.9 10.7396C14.61 11.0296 14.61 11.5096 14.9 11.7996C15.05 11.9496 15.24 12.0196 15.43 12.0196C15.62 12.0196 15.81 11.9496 15.96 11.7996L17.53 10.2296C17.82 9.92957 17.82 9.45957 17.53 9.15957Z"/>
  </svg>
);

// ── Parser ────────────────────────────────────────────────────

function cvDataZuParseResult(cvData) {
  const sektionen = (cvData.sektionen || []).map((s) => {
    const lines = [];
    if (s.typ === 'eintraege') {
      for (const e of (s.eintraege || [])) {
        lines.push(`DATUM: ${e.datum}`);
        lines.push(`JOBTITEL: ${e.jobtitel}`);
        lines.push(`ARBEITGEBER: ${e.arbeitgeber}`);
        for (const b of (e.bullets || [])) lines.push(`BULLET: ${b}`);
      }
    } else if (s.typ === 'kenntnisse') {
      for (const k of (s.kenntnisse || [])) {
        if (k.kategorie) lines.push(`KKATEGORIE: ${k.kategorie}`);
        lines.push(`KTITEL: ${k.titel}`);
        lines.push(`KBESCHR: ${k.beschreibung}`);
      }
    } else {
      for (const b of (s.bullets || [])) lines.push(`BULLET: ${b}`);
    }
    return { ...s, inhaltRaw: lines.join('\n') };
  });
  return {
    profil: cvData.profil_text || '',
    sektionen,
    keywordMarkierungen: cvData.keyword_markierungen || [],
  };
}

function parseCvSource(stelle) {
  if (stelle?.cv_data) return cvDataZuParseResult(stelle.cv_data);
  if (stelle?.cv_rohtext) return parseRohtext(stelle.cv_rohtext);
  return { profil: '', sektionen: [], keywordMarkierungen: [] };
}

function parseRohtext(rohtext) {
  const text = rohtext.replace(/\s*---\s*$/, '').trim();
  const teile = text.split(/\n?## /);

  const profilTeil = teile[0].trim();
  const profil = profilTeil.startsWith('PROFIL:')
    ? profilTeil.slice(7).trim()
    : profilTeil;

  const sektionen = teile.slice(1).map((teil) => {
    const zeilen = teil.split('\n');
    const name = zeilen[0].trim();
    const inhaltZeilen = zeilen.slice(1).map((z) => z.trim()).filter(Boolean);
    const inhaltRaw = zeilen.slice(1).join('\n').trim();
    return { name, inhaltRaw, ...parseSektionInhalt(inhaltZeilen) };
  });

  return { profil, sektionen, keywordMarkierungen: [] };
}

function parseSektionInhalt(zeilen) {
  if (zeilen.some((z) => z.startsWith('KTITEL:'))) {
    const kenntnisse = [];
    let current = null;
    let aktuelleKategorie = '';
    for (const z of zeilen) {
      if (z.startsWith('KKATEGORIE:')) {
        aktuelleKategorie = z.slice(11).trim();
        if (current) current.kategorie = aktuelleKategorie;
      } else if (z.startsWith('KTITEL:')) {
        current = { kategorie: aktuelleKategorie, titel: z.slice(7).trim(), beschreibung: '' };
        kenntnisse.push(current);
      } else if (z.startsWith('KBESCHR:') && current) {
        current.beschreibung = z.slice(8).trim();
      }
    }
    return { typ: 'kenntnisse', kenntnisse };
  }

  const hatFirma = zeilen.some((z) => z.startsWith('FIRMA:'));
  const hatEintrag =
    hatFirma ||
    zeilen.some((z) => z.startsWith('DATUM:') || z.startsWith('JOBTITEL:'));

  if (hatEintrag) {
    const eintraege = [];
    let current = null;

    for (const z of zeilen) {
      if (hatFirma && z.startsWith('FIRMA:')) {
        current = { firma: z.slice(6).trim(), datum: '', rolle: '', bullets: [] };
        eintraege.push(current);
      } else if (!hatFirma && z.startsWith('DATUM:')) {
        current = { datum: z.slice(6).trim(), jobtitel: '', arbeitgeber: '', bullets: [] };
        eintraege.push(current);
      } else if (current) {
        if (z.startsWith('DATUM:')) current.datum = z.slice(6).trim();
        else if (z.startsWith('ROLLE:')) current.rolle = z.slice(6).trim();
        else if (z.startsWith('JOBTITEL:')) current.jobtitel = z.slice(9).trim();
        else if (z.startsWith('ARBEITGEBER:')) current.arbeitgeber = z.slice(12).trim();
        else if (z.startsWith('BULLET:')) current.bullets.push(z.slice(7).trim());
      }
    }
    return { typ: 'eintraege', eintraege };
  }

  const bullets = zeilen
    .filter((z) => z.startsWith('BULLET:'))
    .map((z) => z.slice(7).trim());
  return { typ: 'bullets', bullets };
}

// ── Drag Wrapper ──────────────────────────────────────────────

const stableDropAnimation = (args) => {
  if (args.wasDragging) return false;
  return defaultAnimateLayoutChanges(args);
};

function SortableSektionWrapper({ sId, children, onEditClick, showTapHint }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: sId,
    animateLayoutChanges: stableDropAnimation,
  });

  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={`cv-sektion-sortable is-mobile${isDragging ? ' is-dragging' : ''}`}
      {...attributes}
      {...listeners}
      onClick={onEditClick}
    >
      <div className={`tap-zone-overlay${showTapHint ? ' is-pulsing' : ''}`} />
      {children}
    </div>
  );
}

// ── Seiten-Berechnung ─────────────────────────────────────────

const PAGE_CONTENT_HEIGHT = 1003;
const FOOTER_RESERVED = 34;

function berechneSeitenverteilung(measureEl, sektionen, templateId = 'modern') {
  // Reale Bausteinhöhen aus echter Geometrie statt fester Schätz-Aufschläge:
  // (nächster.offsetTop − dieser.offsetTop) = echter Platzverbrauch inkl. Abstand.
  // Verhindert die überzählige, fast leere zweite Seite. Spiegelt CVVorschau.js.
  const kopfEl = measureEl.querySelector('[data-role="kopf"]');
  const units = [];
  if (kopfEl) units.push({ key: 'kopf', el: kopfEl });
  for (let i = 0; i < sektionen.length; i++) {
    const s = sektionen[i];
    if (s.typ === 'kenntnisse' || s.typ === 'bullets') {
      const el = measureEl.querySelector(`[data-sektion="${i}"]`);
      if (el) units.push({ key: `sektion-${i}`, el });
    } else if (s.typ === 'eintraege') {
      const titelEl = measureEl.querySelector(`[data-sektion-titel="${i}"]`);
      if (titelEl) units.push({ key: `titel-${i}`, el: titelEl });
      for (let j = 0; j < s.eintraege.length; j++) {
        const eintragEl = measureEl.querySelector(`[data-eintrag="${i}-${j}"]`);
        if (eintragEl) units.push({ key: `eintrag-${i}-${j}`, el: eintragEl });
      }
    }
  }
  const hoehe = {};
  for (let u = 0; u < units.length; u++) {
    const top = units[u].el.offsetTop;
    const next = u + 1 < units.length
      ? units[u + 1].el.offsetTop
      : top + units[u].el.offsetHeight;
    hoehe[units[u].key] = Math.max(0, next - top);
  }

  const headerH = kopfEl ? hoehe['kopf'] : 150;
  const available = PAGE_CONTENT_HEIGHT - (templateId === 'standard' ? FOOTER_RESERVED : 0);

  const pages = [];
  let aktuelleSeite = { hatKopf: true, teile: [], verbleibend: available - headerH };

  function neueSeite() {
    pages.push(aktuelleSeite);
    aktuelleSeite = { hatKopf: false, teile: [], verbleibend: available };
  }

  for (let i = 0; i < sektionen.length; i++) {
    const s = sektionen[i];

    if (s.typ === 'kenntnisse' || s.typ === 'bullets') {
      const h = hoehe[`sektion-${i}`] ?? 80;
      if (h > aktuelleSeite.verbleibend && aktuelleSeite.teile.length > 0) {
        neueSeite();
      }
      aktuelleSeite.teile.push({ sektionIdx: i, zeigeTitel: true, vonEintrag: 0, bisEintrag: s.bullets?.length ?? 0 });
      aktuelleSeite.verbleibend -= h;

    } else if (s.typ === 'eintraege') {
      const titelH = hoehe[`titel-${i}`] ?? 25;

      if (titelH > aktuelleSeite.verbleibend && aktuelleSeite.teile.length > 0) {
        neueSeite();
      }
      aktuelleSeite.verbleibend -= titelH;

      let vonEintrag = 0;
      let zeigeTitel = true;

      for (let j = 0; j < s.eintraege.length; j++) {
        const eintragH = hoehe[`eintrag-${i}-${j}`] ?? 50;

        if (eintragH > aktuelleSeite.verbleibend) {
          if (j === vonEintrag) {
            aktuelleSeite.verbleibend += titelH;
            neueSeite();
            aktuelleSeite.verbleibend -= titelH;
          } else {
            aktuelleSeite.teile.push({ sektionIdx: i, zeigeTitel, vonEintrag, bisEintrag: j });
            neueSeite();
            vonEintrag = j;
            zeigeTitel = false;
          }
        }
        aktuelleSeite.verbleibend -= eintragH;
      }

      aktuelleSeite.teile.push({ sektionIdx: i, zeigeTitel, vonEintrag, bisEintrag: s.eintraege.length });
    }
  }
  pages.push(aktuelleSeite);
  return pages;
}

// ── Scale Hook ────────────────────────────────────────────────

const CV_BACKDROP_PADDING = 16; // px on each side, outside the scaled content

function useContainerScale(containerRef) {
  const [scale, setScale] = useState(() => {
    const w = (typeof window !== 'undefined' ? window.innerWidth : 375) - CV_BACKDROP_PADDING * 2;
    return Math.min(Math.max(w / 794, 0.1), 1);
  });
  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const update = () => {
      const w = el.offsetWidth;
      if (w < CV_BACKDROP_PADDING * 2 + 1) return;
      const available = w - CV_BACKDROP_PADDING * 2;
      setScale(Math.min(available / 794, 1));
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [containerRef]);
  return scale;
}

// ── Haupt-Component ───────────────────────────────────────────

const MobileCVVorschau = forwardRef(function MobileCVVorschau({ stelle, onRebuildDone, onEditToggle, onUndo, onRedo, canUndo, canRedo, onFotoChange }, ref) {
  const [nutzer, setNutzer] = useState(null);
  const [pages, setPages] = useState(null);
  const [showTapHint, setShowTapHint] = useState(
    () => !sessionStorage.getItem('cv_tap_hint_shown')
  );
  const [fotoPickerOffen, setFotoPickerOffen] = useState(false);
  const [cropperBild, setCropperBild] = useState(null);
  const [cropperNeuesFile, setCropperNeuesFile] = useState(null);
  const measureRef = useRef(null);
  const outerRef = useRef(null);
  const scale = useContainerScale(outerRef);

  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 250, tolerance: 5 } }),
  );

  useEffect(() => {
    if (!showTapHint) return;
    const t = setTimeout(() => {
      sessionStorage.setItem('cv_tap_hint_shown', '1');
      setShowTapHint(false);
    }, 1600);
    return () => clearTimeout(t);
  }, [showTapHint]);

  useEffect(() => {
    if (!stelle?.profil_name) return;
    getProfil(stelle.profil_name)
      .then((r) => setNutzer(r.data))
      .catch(() => {});
  }, [stelle?.profil_name]);

  const effectiveNutzer = useMemo(() => {
    if (nutzer) {
      return {
        ...nutzer,
        telefon: nutzer.telefon || stelle?.absender_telefon,
        email: nutzer.email || stelle?.absender_email,
        anschrift: (nutzer.strasse || nutzer.plz) ? undefined : stelle?.absender_anschrift,
      };
    }
    if (stelle?.absender_name) {
      return {
        name: stelle.absender_name,
        anschrift: stelle.absender_anschrift,
        telefon: stelle.absender_telefon,
        email: stelle.absender_email,
      };
    }
    return null;
  }, [nutzer, stelle]);

  const hasCvData = !!(stelle?.cv_data || stelle?.cv_rohtext);

  useLayoutEffect(() => {
    if (!measureRef.current || !hasCvData) return;
    setPages(null);
    const { sektionen } = parseCvSource(stelle);
    const templateId = inferTemplateId(stelle, sektionen);
    const verteilung = berechneSeitenverteilung(measureRef.current, sektionen, templateId);
    setPages(verteilung);
  }, [stelle?.id, stelle?.cv_data, stelle?.cv_rohtext, effectiveNutzer]);

  if (!hasCvData) return null;

  const { profil: profilText, sektionen, keywordMarkierungen } = parseCvSource(stelle);
  const cvMarkierungen = normalisiereCvMarkierungen(keywordMarkierungen);
  const { Kopf, Sektion, Footer } = getTemplate(inferTemplateId(stelle, sektionen));

  const fallback = [{
    hatKopf: true,
    teile: sektionen.map((s, i) => ({ sektionIdx: i, zeigeTitel: true, vonEintrag: 0, bisEintrag: s.eintraege?.length ?? s.bullets?.length ?? 0 })),
    verbleibend: 0,
  }];
  const seitenLayout = (pages && pages[0]?.teile &&
    pages.every(p => p.teile.every(t => t.sektionIdx < sektionen.length)))
    ? pages : fallback;

  const numPages = seitenLayout.length;
  // scaled content height + unscaled outer padding top + bottom
  const wrapperHeight = Math.round(
    (1123 * numPages + 24 * (numPages - 1)) * scale + CV_BACKDROP_PADDING * 2
  );

  const onDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;

    const oldIdx = parseInt(active.id.replace('s-', ''), 10);
    const newIdx = parseInt(over.id.replace('s-', ''), 10);
    const neueSektionen = arrayMove(sektionen, oldIdx, newIdx);

    if (stelle.cv_data) {
      const neuesCvData = {
        ...stelle.cv_data,
        sektionen: neueSektionen.map(({ inhaltRaw: _, ...rest }) => rest),
      };
      if (onRebuildDone) onRebuildDone(neuesCvData);
      rebuildDokument(stelle.id, neueSektionen.map((s) => s.name)).catch(() => {});
    } else {
      const headerPart = (stelle.cv_rohtext || '').split('\n## ')[0].trim();
      const body = neueSektionen.map((s) => `## ${s.name}\n${s.inhaltRaw}`).join('\n');
      const neuerRohtext = headerPart ? `${headerPart}\n${body}` : body;
      if (onRebuildDone) onRebuildDone(neuerRohtext);
      rebuildDokument(stelle.id, neueSektionen.map((s) => s.name)).catch(() => {});
    }
  };

  const handleFotoGewaehlt = ({ type, url, file }) => {
    setFotoPickerOffen(false);
    setCropperBild(url);
    setCropperNeuesFile(type === 'neu' ? file : null);
  };

  const handleCropFertig = async ({ url, storagePath }) => {
    if (cropperBild?.startsWith('blob:')) URL.revokeObjectURL(cropperBild);
    setCropperBild(null);
    setCropperNeuesFile(null);
    await updateStelle(stelle.id, { foto_url: url, foto_storage_path: storagePath }).catch(() => {});
    if (onFotoChange) onFotoChange(url);
  };

  const handleFotoEntfernen = async () => {
    await updateStelle(stelle.id, { foto_url: null, foto_storage_path: null }).catch(() => {});
    if (onFotoChange) onFotoChange(null);
  };

  return (
    <div className="cv-vorschau-wrapper">
      <div className="cv-vorschau-label">
        <span>Lebenslauf Vorschau</span>
        {(onUndo || onRedo) && (
          <div className="cv-history-btns">
            <button className="cv-history-btn" onClick={onUndo} disabled={!canUndo} title="Rückgängig">
              <UndoIcon />
            </button>
            <button className="cv-history-btn" onClick={onRedo} disabled={!canRedo} title="Wiederherstellen">
              <RedoIcon />
            </button>
          </div>
        )}
      </div>

      {onRebuildDone && stelle?.foto_url && (
        <div className="cv-foto-feld">
          <button
            className="cv-foto-feld-btn cv-foto-feld-btn--aktiv"
            type="button"
            onClick={() => setFotoPickerOffen(true)}
          >
            <img src={stelle.foto_url} alt="" className="cv-foto-feld-thumb" />
            <span className="cv-foto-feld-text">
              <strong>Foto ändern</strong>
              <span>Wird in der Standard-Vorlage rechts platziert</span>
            </span>
          </button>
          <button className="cv-foto-feld-remove" type="button" onClick={handleFotoEntfernen}>
            Entfernen
          </button>
        </div>
      )}

      {/* Versteckter Mess-Container — immer bei voller A4-Breite */}
      <div ref={measureRef} className="cv-measure-hidden">
        <div data-role="kopf">
          <Kopf profil={profilText} nutzer={effectiveNutzer} fotoUrl={stelle?.foto_url} onFotoClick={null} markierungen={cvMarkierungen} />
        </div>
        {sektionen.map((s, i) => (
          <div key={i} data-sektion={i}>
            <Sektion s={s} sektionIdx={i} markierungen={cvMarkierungen} fotoUrl={i === 0 ? (stelle?.foto_url || null) : null} />
          </div>
        ))}
      </div>

      {/* Skalierter Viewport-Container */}
      <div
        ref={outerRef}
        style={{
          width: '100%',
          overflow: 'hidden',
          height: wrapperHeight,
          background: 'var(--bg-surface)',
          borderRadius: 'var(--radius-island)',
          boxSizing: 'border-box',
          padding: `${CV_BACKDROP_PADDING}px`,
        }}
      >
        <div style={{
          transform: `scale(${scale})`,
          transformOrigin: 'top left',
          width: 794,
          WebkitTextSizeAdjust: '100%',
          textSizeAdjust: '100%',
        }}>
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
            <SortableContext items={sektionen.map((_, i) => `s-${i}`)} strategy={verticalListSortingStrategy}>
              <div className="cv-paginas-container cv-paginas-mobile" ref={ref}>
                {seitenLayout.map((page, pi) => (
                  <div key={pi} className="cv-pagina">
                    {page.hatKopf && (
                      <Kopf
                        profil={profilText}
                        nutzer={effectiveNutzer}
                        fotoUrl={stelle?.foto_url || null}
                        onFotoClick={onRebuildDone ? () => setFotoPickerOffen(true) : null}
                        markierungen={cvMarkierungen}
                      />
                    )}
                    {page.teile.map((teil, ti) => {
                      const s = sektionen[teil.sektionIdx];
                      if (!s) return null;
                      const showDrag = teil.zeigeTitel && !!onRebuildDone;
                      const sectionFotoUrl = pi === 0 && ti === 0 ? (stelle?.foto_url || null) : null;

                      if (showDrag) {
                        return (
                          <SortableSektionWrapper
                            key={`${pi}-${ti}`}
                            sId={`s-${teil.sektionIdx}`}
                            onEditClick={() => onEditToggle && onEditToggle(teil.sektionIdx, s)}
                            showTapHint={showTapHint}
                          >
                            <Sektion
                              s={s}
                              sektionIdx={teil.sektionIdx}
                              zeigeTitel={teil.zeigeTitel}
                              vonEintrag={teil.vonEintrag}
                              bisEintrag={teil.bisEintrag}
                              markierungen={cvMarkierungen}
                              fotoUrl={sectionFotoUrl}
                            />
                          </SortableSektionWrapper>
                        );
                      }

                      return (
                        <Sektion
                          key={`${pi}-${ti}`}
                          s={s}
                          sektionIdx={teil.sektionIdx}
                          zeigeTitel={teil.zeigeTitel}
                          vonEintrag={teil.vonEintrag}
                          bisEintrag={teil.bisEintrag}
                          markierungen={cvMarkierungen}
                          fotoUrl={sectionFotoUrl}
                        />
                      );
                    })}
                    <Footer nutzer={effectiveNutzer} />
                  </div>
                ))}
              </div>
            </SortableContext>
          </DndContext>
        </div>
      </div>
      {fotoPickerOffen && (
        <FotoPickerModal
          onFotoGewaehlt={handleFotoGewaehlt}
          onSchliessen={() => setFotoPickerOffen(false)}
        />
      )}
      {cropperBild && (
        <FotoCropperModal
          bildUrl={cropperBild}
          neuesFile={cropperNeuesFile}
          onFertig={handleCropFertig}
          onAbbrechen={() => { if (cropperBild?.startsWith('blob:')) URL.revokeObjectURL(cropperBild); setCropperBild(null); setCropperNeuesFile(null); }}
        />
      )}
    </div>
  );
});

export default MobileCVVorschau;
